import { Receipt } from "@/types/receipt";

import { translate, formatDate, formatPercentage } from "@/utils/formatters";

import styles from "./InvoiceSection.module.css";

interface InvoiceSectionProps {
  receipt: Receipt;
  lang: string;
}

const getControlNumberLabel = (type: string): string => {
  switch (type.toLowerCase()) {
    case "no_kid":
      return "KID";
    case "dk_kid":
      return "FIK";
    case "se_ocr":
      return "OCR";
    default:
      return type;
  }
};

export const InvoiceSection = ({ receipt, lang }: InvoiceSectionProps) => {
  if (!receipt.invoices || receipt.invoices.length === 0) {
    return null;
  }

  return (
    <>
      {receipt.invoices.map((invoice, index) => (
        <div
          key={`invoice-${index}-${invoice.invoice_number}`}
          className="box white"
        >
          <div className="split">
            <div>
              <p>
                {translate(lang, "Fakturainformasjon", "Invoice Information")}
              </p>
            </div>
          </div>

          <div>
            <p className="amount small">
              <span>
                {translate(lang, "Fakturanummer: ", "Invoice Number: ")}
              </span>
              {invoice.invoice_number}
            </p>

            <p className="amount small">
              <span>
                {translate(lang, "Kontonummer: ", "Account Number: ")}
              </span>
              {invoice.account_number}
            </p>

            {invoice.control_number && (
              <p className="amount small">
                <span>
                  {getControlNumberLabel(invoice.control_number.type)}:{" "}
                </span>
                {invoice.control_number.value}
              </p>
            )}

            {invoice.expired_at && (
              <p className="amount small">
                <span>{translate(lang, "Utløper: ", "Expires: ")}</span>
                {formatDate(invoice.expired_at, receipt.merchant.country_code)}
              </p>
            )}

            {invoice.vat_amount !== undefined &&
              invoice.vat_percentage !== undefined && (
                <p className="amount small">
                  <span>{translate(lang, "MVA: ", "VAT: ")}</span>
                  {invoice.vat_amount} (
                  {formatPercentage(invoice.vat_percentage)})
                </p>
              )}

            {invoice.payment_amount !== undefined && (
              <p className="amount small">
                <span>{translate(lang, "Beløp: ", "Amount: ")}</span>
                {invoice.payment_amount} {invoice.currency}
              </p>
            )}

            {invoice.seller && (
              <>
                <p className={styles.sellerHeading}>
                  {translate(lang, "Selgerinformasjon", "Seller Information")}
                </p>

                {invoice.seller.org_number && (
                  <p className="amount small">
                    <span>
                      {translate(
                        lang,
                        "Organisasjonsnummer: ",
                        "Organization Number: "
                      )}
                    </span>
                    {invoice.seller.org_number}
                  </p>
                )}

                {invoice.seller.country_code && (
                  <p className="amount small">
                    <span>{translate(lang, "Land: ", "Country: ")}</span>
                    {invoice.seller.country_code}
                  </p>
                )}

                {invoice.seller.contact_info && (
                  <>
                    {invoice.seller.contact_info.name && (
                      <p className="amount small">
                        <span>{translate(lang, "Navn: ", "Name: ")}</span>
                        {invoice.seller.contact_info.name}
                      </p>
                    )}
                    {invoice.seller.contact_info.address && (
                      <p className="amount small">
                        <span>{translate(lang, "Adresse: ", "Address: ")}</span>
                        {invoice.seller.contact_info.address}
                      </p>
                    )}
                    {invoice.seller.contact_info.email && (
                      <p className="amount small">
                        <span>{translate(lang, "Epost: ", "Email: ")}</span>
                        {invoice.seller.contact_info.email}
                      </p>
                    )}
                    {invoice.seller.contact_info.phone_number && (
                      <p className="amount small">
                        <span>{translate(lang, "Telefon: ", "Phone: ")}</span>
                        {invoice.seller.contact_info.phone_number}
                      </p>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      ))}
    </>
  );
};
